import React from 'react';
import { useTranslation } from 'react-i18next';
import { APP_NAME } from '../../constants';
import './ThirdPartyModelDisclaimer.css';

/**
 * TODO(legal): replace with the published HelpX page URL once available.
 * Legal requires "Learn more" to link to the HelpX page describing third-party
 * model support. Until the page is published the link is not rendered.
 */
export const THIRD_PARTY_MODELS_HELPX_URL = '';

export interface ThirdPartyModelDisclaimerProps {
  /**
   * `inline` suits dialogs and drawers where vertical space is tight;
   * `block` suits full pages and tables.
   */
  variant?: 'block' | 'inline';
  className?: string;
}

/**
 * Legal-mandated disclaimer for third-party AI model brand names.
 *
 * Product Legal requires this wherever third-party model or provider names
 * (Azure OpenAI, Gemini, IBM watsonx) are shown, to make clear Adobe does not
 * own, control, or operate those services and that their inclusion implies no
 * partnership, endorsement, or sponsorship.
 *
 * The wording is approved by Legal — do not reword without Legal review. Keep
 * provider references as plain text; logos and other branding must not be used.
 */
export const ThirdPartyModelDisclaimer: React.FC<ThirdPartyModelDisclaimerProps> = ({
  variant = 'block',
  className = '',
}) => {
  const { t } = useTranslation();
  return (
    <p
      className={`third-party-model-disclaimer third-party-model-disclaimer--${variant} ${className}`.trim()}
      data-testid="third-party-model-disclaimer"
    >
      {t('components.thirdPartyModelDisclaimer.body', { appName: APP_NAME })}
      {THIRD_PARTY_MODELS_HELPX_URL ? (
        <>
          {' '}
          <a
            href={THIRD_PARTY_MODELS_HELPX_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="third-party-model-disclaimer__link"
          >
            {t('components.thirdPartyModelDisclaimer.learnMore')}
          </a>
          .
        </>
      ) : null}
    </p>
  );
};
